import {observer} from 'mobx-react-lite'
import React, {useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {ExtensionsGalleryPromoBanner} from '@components/dashboard/banners/go-to-extensions'
import {ImportFromTrelloBanner} from '@components/dashboard/banners/import-from-trello'
import {useBannerType} from '@components/dashboard/banners/useBannerType'
import {
  CompanyProjectsContext,
  CompanyProjectsStore
} from '@components/dashboard/company-projects/company-projects-store'
import {DashboardTitle} from '@components/dashboard/dashboard-title'
import {PageComponent} from '@components/page/page-component'
import {useSyncEventUpdateEffect} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {apiBlockVisibilityStore} from '../../api/components/api-block-visibility'
import {CompanyDepartments} from './company-departments/company-departments'
import {CompanyProjects} from './company-projects/company-projects'
import {EditIdProjectPopup} from './company-projects/project-list/edit-id-project-popup'
import {CompanySettings} from './company-settings/company-settings'
import {CompanyUsers} from './company-users'
import {isBlocked} from './dashboard-utils'

const TwWrapReverse = tw.div`
  flex
  flex-wrap
  flex-row-reverse
  gap-16
  max-w-1096
`

const TwCol = tw.div`
  flex-auto
  gap-16
  flex
  flex-col
`

const TwCompanySettings = tw(CompanySettings)`
  min-w-[792px]
  max-w-1096
`

const TwCompanyProjects = tw(CompanyProjects)`
  min-w-[792px]
  max-w-1096
`

const TwCompanyDepartments = tw(CompanyDepartments)`
  min-w-[792px]
  max-w-1096
`

const TwCompanyUsers = tw(CompanyUsers)`
  min-w-[792px]
  max-w-1096
`

const TwImportFromTrelloBanner = tw(ImportFromTrelloBanner)`
  min-w-[792px]
  max-w-1096
`

const TwWrapCol = tw.div`
  flex
  flex-col
  h-full
  w-fit
`

const TwWrapMargins = tw.div`
  mr-24
  mt-85
  max-w-1096
`

const TwWrapColGap = tw.div`
  flex
  flex-col
  gap-16
  mb-24
`

const DashboardPageBase = observer(() => {
  useSyncEventUpdateEffect('current', {
    debounceTime: 50,
    filter: '*'
  })

  const [extensionsPopupOpen, setExtensionsPopupOpen] = useState(false)
  const [bannerType, invalidateBanner] = useBannerType()

  const showCompanyDepartments =
    apiBlockVisibilityStore.state.companyDepartments

  const showCompanyUsers = apiBlockVisibilityStore.state.companyUsers

  return (
    <PageComponent title={<DashboardTitle />} scrollable topZero>
      <TwWrapCol>
        <TwWrapMargins>
          <TwWrapColGap>
            {isBlocked(globals.currentUserId) && (
              <div className="prj__you-blocked-hint">
                {Lang.msg.youBlockedHint}
              </div>
            )}
            <TwCompanySettings
              extensionsPopupOpen={extensionsPopupOpen}
              setExtensionsPopupOpen={setExtensionsPopupOpen}
            />
            {bannerType === 'trello' && (
              <TwImportFromTrelloBanner onClose={invalidateBanner} />
            )}

            {bannerType === 'extensions' && (
              <ExtensionsGalleryPromoBanner
                setExtensionsPopupOpen={setExtensionsPopupOpen}
                onClose={invalidateBanner}
              />
            )}

            <TwCompanyProjects />
            {(showCompanyDepartments || showCompanyUsers) && (
              <TwWrapReverse>
                <TwCol>
                  {showCompanyDepartments && <TwCompanyDepartments />}
                  {showCompanyUsers && <TwCompanyUsers />}
                </TwCol>
              </TwWrapReverse>
            )}
          </TwWrapColGap>
        </TwWrapMargins>
      </TwWrapCol>
    </PageComponent>
  )
})

export const DashboardPage = () => {
  const [store] = useState(() => new CompanyProjectsStore())

  return (
    <CompanyProjectsContext.Provider value={store}>
      <DashboardPageBase />

      <EditIdProjectPopup />
    </CompanyProjectsContext.Provider>
  )
}
